import { useEffect, useMemo, useState } from 'react';

import { collection, onSnapshot } from 'firebase/firestore';

import { FSCollections } from '@src/constants/database';
import { db } from '@src/firebase/firebase';
import type { WorkJourneyAssignedUser, WorkJourneyForm } from '@src/packages/workJourney/types';

type SubcollectionState = Record<
  string,
  {
    members?: WorkJourneyAssignedUser[];
    supervisors?: WorkJourneyAssignedUser[];
  }
>;

const SUBCOLLECTIONS = [
  FSCollections.WORK_JOURNEY_MEMBERS,
  FSCollections.WORK_JOURNEY_SUPERVISORS,
] as const;

export const useWorkJourneyConfigSubcollections = (
  orgId: string,
  parentDocs: WorkJourneyForm[],
): WorkJourneyForm[] => {
  const [subcollectionState, setSubcollectionState] = useState<SubcollectionState>({});

  const parentDocIds = useMemo(
    () =>
      parentDocs
        .map(doc => doc.id)
        .filter((id): id is string => typeof id === 'string' && id.length > 0)
        .sort()
        .join(','),
    [parentDocs],
  );

  useEffect(() => {
    if (!orgId || !parentDocIds) {
      setSubcollectionState({});
      return undefined;
    }

    const docIds = parentDocIds.split(',').filter(Boolean);
    const unsubs: Array<() => void> = [];

    docIds.forEach(docId => {
      SUBCOLLECTIONS.forEach(subcollectionName => {
        const subcollectionRef = collection(
          db,
          FSCollections.ORGANIZATIONS,
          orgId,
          FSCollections.WORK_JOURNEY_CONFIGS,
          docId,
          subcollectionName,
        );

        const unsub = onSnapshot(subcollectionRef, snapshot => {
          const items = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
          })) as WorkJourneyAssignedUser[];

          setSubcollectionState(prev => ({
            ...prev,
            [docId]: {
              ...prev[docId],
              [subcollectionName]: items,
            },
          }));
        });

        unsubs.push(unsub);
      });
    });

    return () => {
      unsubs.forEach(unsub => unsub());
    };
  }, [orgId, parentDocIds]);

  return useMemo(
    () =>
      parentDocs.map(doc => {
        const docId = doc.id;
        if (!docId) return doc;

        const subcollections = subcollectionState[docId];

        return {
          ...doc,
          members: subcollections?.[FSCollections.WORK_JOURNEY_MEMBERS] ?? doc.members ?? [],
          supervisors:
            subcollections?.[FSCollections.WORK_JOURNEY_SUPERVISORS] ?? doc.supervisors ?? [],
        };
      }),
    [parentDocs, subcollectionState],
  );
};
